import * as THREE from 'three';
import type { GameContext } from '../../core/context';
import type { System } from '../../core/system';
import { carTraits } from '../carTraits';
import { getVehicleParts } from '../meshes';
import type { Vehicle } from '../types';
import type { VehicleService } from '../vehicleSystem';
import type { ParticleService } from './particles';
import { smokeShade } from '../../fx/vfxTuning';

/**
 * Per-frame vehicle effects driven by telemetry: pooled skid-mark decals (one draw call), tyre
 * smoke, wheelspin smoke, engine smoke / fire for damaged cars. Sparks and debris are emitted at
 * the moment of contact by the vehicle post-physics system.
 */
const MAX_SEGMENTS = 4000;
const SKID_THRESHOLD = 0.28;
const MIN_SEG_LEN = 0.35;
/** Exhaust smoke is only worth emitting near the camera. */
const EXHAUST_DIST2 = 75 * 75;

/** Per-car exhaust character: tired engines smoke, every engine smokes a little from cold. */
interface Exhaust {
  age: number;
  diesel: boolean;
  /** Seconds the engine has been running (warms up over the first minute). */
  run: number;
}

interface WheelTrail {
  x: number;
  z: number;
  valid: boolean;
  /** Metres skidded so far in this continuous slide: marks start faint and darken. */
  len: number;
}

export function createVehicleFxSystem(): System {
  let ctx: GameContext;
  let vehicles: VehicleService;
  let particles: ParticleService | undefined;
  const positions = new Float32Array(MAX_SEGMENTS * 4 * 3);
  const colors = new Float32Array(MAX_SEGMENTS * 4 * 4);
  const geo = new THREE.BufferGeometry();
  const posAttr = new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage);
  const colAttr = new THREE.BufferAttribute(colors, 4).setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('position', posAttr);
  geo.setAttribute('color', colAttr);
  const index = new Uint32Array(MAX_SEGMENTS * 6);
  for (let i = 0; i < MAX_SEGMENTS; i++) {
    index.set([i * 4, i * 4 + 2, i * 4 + 1, i * 4 + 2, i * 4 + 3, i * 4 + 1], i * 6);
  }
  geo.setIndex(new THREE.BufferAttribute(index, 1));
  geo.setDrawRange(0, 0);
  const mat = new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  mesh.renderOrder = 2;
  let head = 0;
  let used = 0;
  let dirtyFrom = -1;
  let dirtyCount = 0;

  const trails = new Map<number, WheelTrail[]>();
  const budgets = new Map<number, number[]>();
  const exhausts = new Map<number, Exhaust>();
  const pose = { x: 0, z: 0, angle: 0 };

  function addSegment(x0: number, z0: number, x1: number, z1: number, hw: number, alpha: number): void {
    const dx = x1 - x0;
    const dz = z1 - z0;
    const l = Math.hypot(dx, dz) || 1;
    const px = (-dz / l) * hw;
    const pz = (dx / l) * hw;
    const i = head;
    const p = i * 12;
    positions[p] = x0 + px;
    positions[p + 1] = 0.025;
    positions[p + 2] = z0 + pz;
    positions[p + 3] = x0 - px;
    positions[p + 4] = 0.025;
    positions[p + 5] = z0 - pz;
    positions[p + 6] = x1 + px;
    positions[p + 7] = 0.025;
    positions[p + 8] = z1 + pz;
    positions[p + 9] = x1 - px;
    positions[p + 10] = 0.025;
    positions[p + 11] = z1 - pz;
    const c = i * 16;
    for (let k = 0; k < 4; k++) {
      colors[c + k * 4] = 0.012;
      colors[c + k * 4 + 1] = 0.012;
      colors[c + k * 4 + 2] = 0.014;
      colors[c + k * 4 + 3] = alpha;
    }
    if (dirtyFrom < 0) {
      dirtyFrom = i;
      dirtyCount = 0;
    }
    dirtyCount++;
    head = (head + 1) % MAX_SEGMENTS;
    used = Math.min(used + 1, MAX_SEGMENTS);
    if (head === 0) {
      // Wrapped: flush everything next upload.
      dirtyFrom = 0;
      dirtyCount = MAX_SEGMENTS;
    }
  }

  function wheelWorld(v: Vehicle, w: number): { x: number; z: number } {
    const s = v.spec;
    const lx = (w % 2 === 0 ? 1 : -1) * (s.width / 2 - 0.22); // local +X = left
    const lz = (w < 2 ? 1 : -1) * (s.wheelbase / 2);
    const c = Math.cos(pose.angle);
    const sn = Math.sin(pose.angle);
    return { x: lx * c + lz * sn + pose.x, z: -lx * sn + lz * c + pose.z };
  }

  function conditionFx(v: Vehicle, dt: number, budget: number[]): void {
    if (!particles || v.condition === 'ok') return;
    const s = v.spec;
    const c = Math.cos(pose.angle);
    const sn = Math.sin(pose.angle);
    const lz = s.body === 'van' || s.body === 'lorry' ? s.length / 2 - 0.9 : s.length / 2 - 1.0;
    const engine = { x: lz * sn + pose.x, y: s.height * 0.7, z: lz * c + pose.z };
    const vel = { x: v.velocity.x, y: 0, z: v.velocity.z };
    const rate = (i: number, perSec: number): number => {
      budget[i] = (budget[i] ?? 0) + perSec * dt;
      const n = Math.floor(budget[i]!);
      budget[i]! -= n;
      return n;
    };
    switch (v.condition) {
      case 'smoking':
        // pale wisps at first, darker as the engine nears the burning point
        particles.emit('engineSmoke', engine, { count: rate(4, 7), vel, color: smokeShade(v.health / s.maxHealth) });
        break;
      case 'heavySmoke':
        particles.emit('blackSmoke', engine, { count: rate(4, 9), vel, intensity: 0.8 });
        particles.emit('embers', engine, { count: rate(5, 2) });
        break;
      case 'burning':
        particles.emit('fire', engine, { count: rate(4, 45), vel, intensity: 1.2 });
        particles.emit('fire', { x: pose.x, y: s.height * 0.5, z: pose.z }, { count: rate(6, 25) });
        particles.emit('blackSmoke', { ...engine, y: engine.y + 1 }, { count: rate(5, 14), vel });
        particles.emit('embers', engine, { count: rate(7, 8) });
        break;
      case 'wrecked':
        particles.emit('blackSmoke', { x: pose.x, y: s.height * 0.6, z: pose.z }, { count: rate(4, v.isPolice ? 5 : 3), intensity: v.isPolice ? 0.9 : 0.7 });
        particles.emit('fire', { x: pose.x, y: 0.4, z: pose.z }, { count: rate(5, v.isPolice ? 9 : 5), intensity: v.isPolice ? 1 : 0.6 });
        break;
    }
  }

  function exhaustFx(v: Vehicle, dt: number, budget: number[]): void {
    if (!particles || v.destroyed || (v.occupant === null && v.controller === 'none')) return;
    let ex = exhausts.get(v.id);
    if (!ex) {
      ex = { age: carTraits(v.id, v.spec).age, diesel: v.spec.body === 'lorry' || v.spec.body === 'van', run: 0 };
      exhausts.set(v.id, ex);
    }
    ex.run += dt;
    const cam = ctx.camera.position;
    const dxc = pose.x - cam.x;
    const dzc = pose.z - cam.z;
    if (dxc * dxc + dzc * dzc > EXHAUST_DIST2) return;
    const tel = v.telemetry;
    const speed = Math.abs(tel.forwardSpeed);
    const cold = Math.max(0, 1 - ex.run / 60);
    let rate = speed < 3 ? 0.3 + 2.2 * ex.age * ex.age + 2.4 * cold : 0;
    let load = 0;
    if (tel.throttle > 0.5 && speed < 16) {
      load = tel.throttle * (0.4 + ex.age + 0.5 * cold + (ex.diesel ? 0.5 : 0));
      rate += 7 * load;
    }
    budget[8] = (budget[8] ?? 0) + rate * dt;
    const n = Math.floor(budget[8]!);
    if (n <= 0) return;
    budget[8]! -= n;
    const outs = getVehicleParts(v.spec).exhaust;
    const c = Math.cos(pose.angle);
    const sn = Math.sin(pose.angle);
    const dark = Math.min(1, load * 0.6 + (ex.diesel ? 0.35 : 0) + ex.age * 0.3);
    const color = dark > 0.5 ? 0x5a5754 : dark > 0.25 ? 0x7c7d80 : 0xa4a8ae;
    for (const o of outs) {
      particles.emit(
        'exhaust',
        { x: o.x * c + o.z * sn + pose.x, y: o.y, z: -o.x * sn + o.z * c + pose.z },
        { count: Math.max(1, Math.round(n / outs.length)), vel: { x: v.velocity.x, y: 0, z: v.velocity.z }, dir: { x: -sn, z: -c }, intensity: 0.7 + load, color },
      );
    }
  }

  return {
    id: 'vehicle-fx',
    order: 720,
    init(c) {
      ctx = c;
      c.scene.add(mesh);
    },
    start() {
      vehicles = ctx.services.get('vehicles');
      particles = ctx.services.tryGet('particles');
    },
    update(dt, alpha) {
      if (dt <= 0) return;
      const live = new Set<number>();
      for (const v of vehicles.all()) {
        live.add(v.id);
        v.body.interpolate(alpha, pose);
        let trail = trails.get(v.id);
        let budget = budgets.get(v.id);
        if (!trail) {
          trail = [0, 1, 2, 3].map(() => ({ x: 0, z: 0, valid: false, len: 0 }));
          trails.set(v.id, trail);
          budget = [0, 0, 0, 0, 0, 0, 0, 0, 0];
          budgets.set(v.id, budget);
        }
        const tel = v.telemetry;
        const speed = v.speed;
        const hw = v.spec.body === 'lorry' ? 0.22 : v.spec.body === 'van' ? 0.17 : 0.14;
        for (let w = 0; w < 4; w++) {
          const sk = tel.wheelSkid[w]!;
          const tr = trail[w]!;
          if (sk < SKID_THRESHOLD || v.body.sleeping) {
            tr.valid = false;
            tr.len = 0;
            continue;
          }
          const p = wheelWorld(v, w);
          if (!tr.valid) {
            tr.x = p.x;
            tr.z = p.z;
            tr.valid = true;
            tr.len = 0;
          } else if (Math.hypot(p.x - tr.x, p.z - tr.z) >= MIN_SEG_LEN) {
            const seg = Math.hypot(p.x - tr.x, p.z - tr.z);
            if (seg < 4) {
              tr.len += seg;
              const ramp = Math.min(1, 0.45 + tr.len * 0.12);
              addSegment(tr.x, tr.z, p.x, p.z, hw * (0.85 + 0.3 * ramp), Math.min(0.9, (0.3 + sk * 0.6) * ramp));
            }
            tr.x = p.x;
            tr.z = p.z;
          }
          // Tyre smoke: proportional to slide and speed. Not off a wreck spun round by its own blast, nor off a
          // parked car shoved sideways by one (the marks stay; pale puffs next to the plume read as steam).
          if (particles && v.condition !== 'wrecked' && (v.occupant !== null || v.controller !== 'none') && sk > 0.4 && (speed > 2.5 || tel.wheelspin > 0.3)) {
            budget![w] = budget![w]! + dt * 26 * sk * Math.min(1.3, 0.4 + speed / 15 + tel.wheelspin);
            const n = Math.floor(budget![w]!);
            budget![w]! -= n;
            if (n > 0) particles.emit('tyreSmoke', { x: p.x, y: 0.2, z: p.z }, { count: n, vel: { x: v.velocity.x, y: 0, z: v.velocity.z }, intensity: sk });
          }
        }
        conditionFx(v, dt, budget!);
        exhaustFx(v, dt, budget!);
      }
      for (const id of trails.keys()) {
        if (!live.has(id)) {
          trails.delete(id);
          budgets.delete(id);
          exhausts.delete(id);
        }
      }
      if (dirtyFrom >= 0) {
        posAttr.clearUpdateRanges();
        colAttr.clearUpdateRanges();
        const end = dirtyFrom + dirtyCount;
        if (end <= MAX_SEGMENTS) {
          posAttr.addUpdateRange(dirtyFrom * 12, dirtyCount * 12);
          colAttr.addUpdateRange(dirtyFrom * 16, dirtyCount * 16);
        } else {
          posAttr.addUpdateRange(dirtyFrom * 12, (MAX_SEGMENTS - dirtyFrom) * 12);
          colAttr.addUpdateRange(dirtyFrom * 16, (MAX_SEGMENTS - dirtyFrom) * 16);
          posAttr.addUpdateRange(0, (end - MAX_SEGMENTS) * 12);
          colAttr.addUpdateRange(0, (end - MAX_SEGMENTS) * 16);
        }
        posAttr.needsUpdate = true;
        colAttr.needsUpdate = true;
        geo.setDrawRange(0, used * 6);
        dirtyFrom = -1;
        dirtyCount = 0;
      }
    },
    dispose() {
      ctx.scene.remove(mesh);
      geo.dispose();
      mat.dispose();
    },
  };
}
